import React, { useState, useEffect } from "react";
import api from "../api";
import DataTable from "../components/DataTable";
import CustomButton from "../components/CustomButton";
import ModalForm from "../components/ModalForm";
import CustomTextInput from "../components/CustomTextInput";
import { useNavigate, useSearchParams } from "react-router-dom";
import Layout from "../components/Layout";

export default function DriversPage() {
  const navigate = useNavigate();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [name, setName] = useState("");
  const [govId, setGovId] = useState("");
  const [email, setEmail] = useState("");
  const [contact, setContact] = useState("");
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [searchParams, setSearchParams] = useSearchParams();

  // Obtener el ordenamiento y búsqueda desde la URL
  const currentSort = searchParams.get("ordering") || "";
  const currentSearch = searchParams.get("search") || "";

  const handleSortChange = (newSort) => {
    // Actualizar la URL con el nuevo ordenamiento
    setSearchParams({ ordering: newSort, search: currentSearch });
  };

  const handleSearchChange = (newSearch) => {
    // Actualizar la URL con el nuevo valor de búsqueda
    setSearchParams({ ordering: currentSort, search: newSearch });
  };

  const columnsConfig = [
    { field: "id", headerName: "ID", sortable: true },
    {
      field: "name",
      headerName: "Nombre del conductor",
      sortable: true,
    },
    { field: "govId", headerName: "Cédula de identidad", sortable: true },
    { field: "email", headerName: "Correo electrónico", sortable: true },

    { field: "phone", headerName: "Número de celular", sortable: true },
  ];

  useEffect(() => {
    async function fetchDrivers() {
      try {
        setLoading(true);
        // Pasamos `currentSort` a `getMulti()`, aprovechando que la API ya soporta ordenamiento
        const response = await api.drivers.getMulti(currentSort);
        setData(response);
      } catch (error) {
        console.error("Error fetching drivers:", error);
      } finally {
        setLoading(false);
      }
    }

    fetchDrivers();
  }, [currentSort]); // Se ejecuta cada vez que cambia `currentSort`

  // Función para manejar el click en "Detalles"
  const handleDetailsClick = (driverId) => {
    navigate(`/drivers/${driverId}`); // Redirige a la página de detalles del conductor
  };

  const handleFormSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError(null);
    setFieldErrors({});

    const newDriver = {
      name: name,
      govId: govId,
      email: email,
      phone: contact,
    };

    try {
      await api.drivers.create(newDriver);
      setIsModalOpen(false);
      const updatedDrivers = await api.drivers.getMulti();
      setData(updatedDrivers);
      setName("");
      setGovId("");
      setEmail("");
      setContact("");
    } catch (error) {
      if (error.message) {
        // Ajuste para errores específicos de campo según la estructura proporcionada
        const fieldErrors = error.message;
        setFieldErrors(fieldErrors);
      } else {
        setError("Error al crear el conductor. Intenta nuevamente.");
      }
    } finally {
      setLoading(false);
    }
  };

  const [opacityLevel, setOpacityLevel] = useState(0);

  useEffect(() => {
    if (loading) {
      let intervalId = setInterval(() => {
        setOpacityLevel((prev) => Math.min(prev + 0.1, 1)); // Incrementa la opacidad hasta 1
      }, 500); // Ajusta el intervalo para controlar la velocidad
      return () => clearInterval(intervalId); // Limpia el intervalo al desmontar
    } else {
      setOpacityLevel(1); // Asegúrate de que la opacidad esté al máximo al terminar de cargar
    }
  }, [loading]);

  return (
    <Layout>
      <div
        className={`p-8 space-y-10 bg-[#F8F9FB] transition-opacity ease-in-out`}
        style={{
          opacity: opacityLevel,
          transition: "opacity 0.5s linear",
        }}
      >
        <h1 className="poppins-bold text-lg text-[#1F384C] tracking-wider">
          Conductores
        </h1>
        <div className="flex justify-between items-center">
          <p className="poppins-regular text-right text-[#595A5C] text-xs tracking-wider ">
            {data.length} conductores registrados
          </p>
          <CustomButton
            text="Agregar conductor"
            onClick={() => setIsModalOpen(true)}
            icon={true}
          />
        </div>

        <DataTable
          columnsConfig={columnsConfig}
          data={data}
          loading={loading}
          onActionClick={(row) => handleDetailsClick(row.id)}
          onSortChange={handleSortChange}
          currentSort={currentSort}
          currentSearch={currentSearch}
          onSearchChange={(e) => handleSearchChange(e.target.value)}
        />
        <ModalForm
          title="Agrega un nuevo conductor"
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSubmit={handleFormSubmit}
          textBtn="Agregar conductor"
        >
          {/* Muestra un error general si existe */}
          {error && <p className="text-red-600 mb-4">{error}</p>}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <CustomTextInput
                label="Nombre del conductor"
                placeholder="Ingresa el nombre del conductor"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                isRequired
              />
              {/* Muestra error específico para el campo "name" */}
              {fieldErrors.name && (
                <p className="text-red-600 text-sm">{fieldErrors.name[0]}</p>
              )}
            </div>
            <div>
              <CustomTextInput
                label="Cédula de identidad"
                placeholder="Ingresa tu número de documento"
                type="text"
                value={govId}
                onChange={(e) => setGovId(e.target.value)}
                isRequired
              />
              {/* Muestra error específico para el campo "govId" */}
              {fieldErrors.govId && (
                <p className="text-red-600 text-sm">{fieldErrors.govId[0]}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <CustomTextInput
                label="Correo electrónico"
                placeholder="Ingresa el correo electrónico"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              {/* Muestra error específico para el campo "email" */}
              {fieldErrors.email && (
                <p className="text-red-600 text-sm">{fieldErrors.email[0]}</p>
              )}
            </div>
            <div>
              <CustomTextInput
                label="Número de celular"
                placeholder="Escribe el número de celular"
                type="text"
                value={contact}
                onChange={(e) => setContact(e.target.value)}
                isRequired
              />
              {/* Muestra error específico para el campo "phone" */}
              {fieldErrors.phone && (
                <p className="text-red-600 text-sm">{fieldErrors.phone[0]}</p>
              )}
            </div>
          </div>
        </ModalForm>
      </div>
    </Layout>
  );
}
